import { Injectable } from '@angular/core';
import {
  ConfigBaseFilter,
  ConfigFilterGroup,
  ConfigService,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  PimTemplateProperties,
  SearchField,
  SelectionAction,
  SimpleConfigBaseFilter
} from 'gung-list';
import { OttoOlsenBlockOrder, OttoOlsenBlockOrderAgreementsService, SigningStatus } from './otto-olsen-block-order-agreements.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, map, Observable, Subject, switchMap } from 'rxjs';
import { OttoOlsenBlockOrderTableListComponent } from '../components/otto-olsen-block-order-table-list/otto-olsen-block-order-table-list.component';
import { MetadataService } from 'gung-standard';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { gungComparatorHelper } from 'gung-common';

export interface GroupedBlockOrderAgreement {
  agreementId: string;
  customerId: string;
  customerName: string;
  offbeskr: string;
  status: string;
  agreementDate: Date;
  agreementValidToDate: Date;
  contact: string;
  rows: OttoOlsenBlockOrder[];
  orderType: number;
  selger?: string;
  signingStatus?: SigningStatus;
}

export class OfferDateFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  type = 'dateRangeFilter';
  typeInDates = true;

  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'BLOCK_ORDER_AGREEMENT_DATE';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {
    let dates = [];
    for (const row of item.rows) {
      if (row.extra.q_gung_ofh?.offertdat) {
        dates.push(format(new Date(row.extra.q_gung_ofh.offertdat), 'yyMMdd'));
      }
    }
    return dates;
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

export class DateAvtaleFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  type = 'dateRangeFilter';

  typeInDates = true;

  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'BLOCK_ORDER_AGREEMENT_VALID_TO_DATE';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {
    let dates = [];
    for (const row of item.rows) {
      if (row.extra.q_gung_ofh?.giltigtomdatum) {
        dates.push(format(new Date(row.extra.q_gung_ofh.giltigtomdatum), 'yyMMdd'));
      }
    }
    return dates;
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

export class BlockOrderTypeFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  constructor() {
    super();
  }

  getName(): string {
    return 'BLOCK_ORDER_STAUS';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {
    return [item.status];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class SalesFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  constructor(protected metadata: MetadataService) {
    super();
  }

  getName(): string {
    return 'SALES';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {
   

    return [item.selger];
  }

  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class OttoOlsenBlockOrderAgreementsListConfigService implements ConfigService<GroupedBlockOrderAgreement> {
  customerId: string;
  topFilter = true;
  selectedColumnSort: Subject<{ field: string; sort: string }> = new BehaviorSubject<{ field: string; sort: string }>(
    null
  );

  constructor(
    protected ottoOlsenBlockOrderAgreementsService: OttoOlsenBlockOrderAgreementsService,
    protected metadataService: MetadataService,
    protected translateService: TranslateService
  ) {}

  getItems(): Observable<GroupedBlockOrderAgreement[]> {
    return this.selectedColumnSort.pipe(
      switchMap(sort => {
        return this.ottoOlsenBlockOrderAgreementsService.getBlockOrders(this.customerId).pipe(
          map(rows => {
            const groupedData: Map<string, GroupedBlockOrderAgreement> = new Map<string, GroupedBlockOrderAgreement>();

            for (const row of rows) {
              if (!groupedData.has(row.extra.q_gung_ofh?.offnr)) {
                const group = {
                  agreementId: row.extra.q_gung_ofh?.offnr,
                  customerId: row.customerId,
                  customerName: row.extra.q_gung_ofh?.ftgnamn,
                  offbeskr: row.extra.q_gung_ofh?.offbeskr,
                  agreementDate: new Date(row.extra.q_gung_ofh?.offertdat),
                  agreementValidToDate: new Date(row.extra.q_gung_ofh?.giltigtomdatum),
                  status: this.metadataService.getMetadataValue(
                    'xa7',
                    'ordstatbeskr',
                    row.extra.q_gung_ofh?.ofh_offstatus
                  ),
                  selger: this.metadataService.getMetadataValue('salj','saljarenamn', row.extra.q_gung_ofh?.saljare),
                  contact: row.extra.q_gung_ofh?.kundref2,
                  refNumber: row.extra.q_gung_ofh?.kundbestnr,
                  orderType: row.extra.q_gung_ofh?.ofh_ordtyp,
                  signingStatus: row.extra.signingStatus,
                  rows: []
                };
                groupedData.set(row.extra.q_gung_ofh?.offnr, group);
              }
              groupedData.get(row.extra.q_gung_ofh?.offnr)?.rows.push(row);
            }
            let dataToTable = [...groupedData.values()];
            this.sortCol(dataToTable, sort);
            return dataToTable;
          })
        );
      })
    );
  }

  sortCol(data: GroupedBlockOrderAgreement[], sort: { field: string; sort: string }) {
    if (sort) {
      data.sort((a, b) => {
        const sortType = sort.sort === 'asc' ? 1 : -1;
        return gungComparatorHelper(a[sort.field], b[sort.field], sortType);
      });
    }
    return data;
  }

  getFilters(): ConfigBaseFilter<GroupedBlockOrderAgreement>[] {
    return [
      new OfferDateFilter(this.translateService),
      new DateAvtaleFilter(this.translateService),
      new BlockOrderTypeFilter(),
      new SalesFilter(this.metadataService)
    ];
  }
  getSortOptions(): ListSortOption<GroupedBlockOrderAgreement>[] {
    return [];
  }
  getBatchSizes(): number[] | undefined {
    return [300];
  }
  getLayouts(): ListLayout<GroupedBlockOrderAgreement>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => OttoOlsenBlockOrderTableListComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'blockorder'
      }
    ];
  }

  getSearchTerms(item: GroupedBlockOrderAgreement): string[] {
    return [item.customerId, item.agreementId, item.customerName];
  }

  getItemId(item: GroupedBlockOrderAgreement): string {
    return item.agreementId;
  }
}
